import React, { useEffect, useRef } from "react";
import { motion, useInView, useMotionValue, useSpring } from "framer-motion";

const STATS = [
  { value: 40, suffix: "+", label: "Years of Manufacturing Excellence" },
  { value: 25, suffix: "+", label: "OEM Partners Across Automotive Sectors" },
  { value: 250, suffix: "+M", label: "Annual Bulb Production" },
  {
    value: 2,
    suffix: "",
    label: "Manufacturing Facilities (Faridabad & Palwal)",
  },
  { value: 20, suffix: "+", label: "Automated Production Lines" },
];
const LOCATIONS = [
  {
    top: "40%",
    left: "69%",
    name: "India",
  },
  {
    top: "40%",
    left: "60%",
    name: "Middle East",
  },
  {
    top: "52%",
    left: "75%",
    name: "South East Asia",
  },
  {
    top: "54%",
    left: "53%",
    name: "Africa",
  },
  {
    top: "60%",
    left: "32%",
    name: "Latin America",
  },
];

// Counter Component for animating numbers
const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const motionValue = useMotionValue(0);
  const springValue = useSpring(motionValue, {
    stiffness: 50,
    damping: 20,
    duration: 2,
  });

  useEffect(() => {
    if (isInView) {
      motionValue.set(value);
    }
  }, [isInView, value, motionValue]);

  useEffect(() => {
    // Update text content directly for performance
    const unsubscribe = springValue.on("change", (latest) => {
      if (ref.current) {
        ref.current.textContent = Math.floor(latest) + suffix;
      }
    });
    return () => unsubscribe();
  }, [springValue, suffix]);

  return <span ref={ref}>0{suffix}</span>;
};

const StatsMapSection = ({ isDesktop, THEME }) => {
  return (
    <section
      id="stats"
      style={{
        backgroundColor: "transparent",
        paddingTop: "50px",
        paddingBottom: "100px",
        // paddingLeft: isDesktop ? "280px" : "0",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          padding: "0 24px",
          position: "relative",
          zIndex: 10,
        }}
      >
        {/* Headline */}
        <div style={{ textAlign: "center", marginBottom: "60px" }}>
          <h2
            style={{
              fontSize: "clamp(32px, 5vw, 48px)",
              fontWeight: 900,
              textTransform: "uppercase",
              color: "#09090b",
              lineHeight: 1.1,
              margin: "0 auto",
              maxWidth: "800px",
            }}
          >
            Our Components Support OEM Platforms Worldwide
          </h2>
        </div>

        {/* Map Container - Uses natural image aspect ratio for perfect dot alignment */}
        <div
          style={{
            position: "relative",
            width: "100%",
            maxWidth: "1000px",
            margin: "0 auto 80px auto",
          }}
        >
          {/* Map Image */}
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/thumb/e/ec/World_map_blank_without_borders.svg/2000px-World_map_blank_without_borders.svg.png"
            alt="World Map"
            style={{
              width: "100%",
              height: "auto", // Important: Allows container to shrink/grow with image
              display: "block",
              // opacity: 0.15,
              filter: "grayscale(100%)",
            }}
          />

          {/* Dots Layer */}
          {LOCATIONS.map((loc, index) => (
            <div
              key={index}
              style={{
                position: "absolute",
                top: loc.top,
                left: loc.left,
                transform: "translate(-50%, -50%)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              {/* Pulsing Animation */}
              <motion.div
                animate={{ scale: [1, 2, 1], opacity: [0.5, 0, 0.5] }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                style={{
                  position: "absolute",
                  width: isDesktop ? "24px" : "16px", // Responsive dot size
                  height: isDesktop ? "24px" : "16px",
                  backgroundColor: "rgba(220, 38, 38, 0.3)",
                  borderRadius: "50%",
                  zIndex: 0,
                }}
              />

              {/* Solid Dot */}
              <div
                style={{
                  width: isDesktop ? "8px" : "6px",
                  height: isDesktop ? "8px" : "6px",
                  backgroundColor: THEME.colors.primary,
                  borderRadius: "50%",
                  position: "relative",
                  zIndex: 2,
                  boxShadow: "0 0 10px rgba(220, 38, 38, 0.5)",
                }}
              ></div>

              {/* Label */}
              {isDesktop && (
                <motion.div
                  initial={{ opacity: 0, y: 5 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  style={{
                    marginTop: "8px",
                    whiteSpace: "nowrap",
                    fontSize: "10px",
                    fontWeight: "800",
                    color: "#52525b",
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    backdropFilter: "blur(4px)",
                    pointerEvents: "none",
                  }}
                >
                  {loc.name}
                </motion.div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Stats Grid with Animated Counters */}
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          padding: "0 24px",
          display: "grid",

          // 🔑 FIX: force 5 columns on desktop
          gridTemplateColumns: isDesktop
            ? "repeat(5, 1fr)"
            : "repeat(auto-fit, minmax(160px, 1fr))",

          gap: isDesktop ? "32px" : "48px",
          textAlign: "center",
          position: "relative",
          zIndex: 20,
        }}
      >
        {STATS.map((stat, index) => (
          <div key={index}>
            <h3
              style={{
                fontSize: "clamp(40px, 4vw, 56px)",

                fontWeight: 900,
                color: THEME.colors.primary,
                margin: 0,
                lineHeight: 1,
                fontFamily: "sans-serif",
                letterSpacing: "-2px",
                display: "flex",
                justifyContent: "center",
                alignItems: "baseline",
              }}
            >
              <Counter value={stat.value} suffix={stat.suffix} />
            </h3>
            <p
              style={{
                fontSize: "14px",
                fontWeight: 700,
                color: "#09090b",
                marginTop: "12px",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
              }}
            >
              {stat.label}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
};

export default StatsMapSection;
